import { useMemo, useState } from 'react';
import { useT } from '@hana/i18n';
import './screens.i18n';
import { IconFilter, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { Button, Segmented, useToast } from '@hana/ui';
import type { SeatKind, Staff, StaffStatus } from './types';
import { isoOfBoardDate } from './logic';
import { STAFF_CONTRACT, STAFF_DEPT_FILTERS, filterStaff, type StaffFilter } from './staffList';
import s from './hr.module.css';

// Mint-HR · tab Nhân sự (design-specs/Mint-HR.md; code board khối staff*). Chỉ đọc; nút ghi báo toast.

const SEAT_TONE: Record<SeatKind, string> = { Primary: s.tMute ?? '', Acting: s.tPri ?? '', Concurrent: s.tWarn ?? '' };
const ST_TONE: Record<StaffStatus, { chip: string; dot: string }> = {
  'Đang làm': { chip: s.tOk ?? '', dot: s.dOk ?? '' },
  'Nghỉ phép': { chip: s.tWarn ?? '', dot: s.dWarn ?? '' },
  'Thử việc': { chip: s.tPri ?? '', dot: s.dPri ?? '' },
};
const fmt1 = (n: number) => String(n).replace('.', ',');
const STATUSES: readonly StaffStatus[] = ['Đang làm', 'Nghỉ phép', 'Thử việc'];
const CONTRACTS = ['HĐ không thời hạn', 'HĐ 36 tháng', 'HĐ 12 tháng', 'Thử việc'];
const SORTS: ReadonlyArray<readonly [string, string]> = [['', 'Mặc định'], ['name', 'Tên A–Z'], ['join', 'Ngày vào mới nhất'], ['leave', 'Phép còn ít nhất']];

function Seats({ st }: { st: Staff }) {
  return (
    <>
      {st.seats.map((h) => (
        <span key={h.code} title={h.kind} className={`${s.seatChip} ${SEAT_TONE[h.kind]}`}>{h.code}</span>
      ))}
    </>
  );
}

export function HrStaff({ staff, total }: { staff: readonly Staff[]; total: number }) {
  const t = useT();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('Tất cả');
  const [view, setView] = useState<'table' | 'card'>('table');
  const [pop, setPop] = useState(false);
  const [f, setF] = useState<StaffFilter>({});
  const rows = useMemo(() => filterStaff(staff, { q, dept, ...f }), [staff, q, dept, f]);
  const nF = Object.values(f).filter(Boolean).length;
  const seatOpts = [...new Set(staff.flatMap((x) => x.seats.map((h) => h.code)))];
  const fields: ReadonlyArray<{ k: string; key: keyof StaffFilter; opts: ReadonlyArray<readonly [string, string]> }> = [
    { k: 'Trạng thái', key: 'st', opts: [['', 'Tất cả'], ...STATUSES.map((v) => [v, v] as const)] },
    { k: 'Chức danh (ghế)', key: 'seat', opts: [['', 'Tất cả'], ...seatOpts.map((v) => [v, v] as const)] },
    { k: 'Loại hợp đồng', key: 'ct', opts: [['', 'Tất cả'], ...CONTRACTS.map((v) => [v, v] as const)] },
    { k: 'Sắp xếp', key: 'sort', opts: SORTS },
  ];
  const noWrite = () => toast({ msg: t('hr.s.noWrite') });
  const count = `${rows.length} / ${total} người`;
  const open = (x: Staff) => () => toast({ msg: `Hồ sơ ${x.id} chưa mở ở bản này` });

  return (
    <>
      <div className={s.sfBar}>
        <label className={s.sfSearch}>
          <IconSearch size={16} stroke={2.2} aria-hidden="true" />
          <input aria-label="Tìm nhân sự" placeholder="Tên, UserName, ghế…" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <Segmented kind="toggle" label="Lọc theo phòng" value={dept} onChange={setDept} items={STAFF_DEPT_FILTERS.map((k) => ({ key: k, label: k }))} />
        <div className={s.sfPopWrap}>
          <button type="button" className={`${s.sfBtn} ${nF ? s.sfBtnOn : ''}`} aria-haspopup="dialog" aria-expanded={pop} onClick={() => setPop(!pop)}>
            <IconFilter size={16} stroke={1.8} aria-hidden="true" />
            {nF ? `Lọc · ${nF}` : '+ Lọc'}
          </button>
          {pop && (
            <>
              <div className={s.sfScrim} aria-hidden="true" onClick={() => setPop(false)} />
              <div role="dialog" aria-label="Lọc nhân sự" className={s.sfPop}>
                {fields.map((fd) => (
                  <label key={fd.key} className={s.sfField}>
                    <div>{fd.k}</div>
                    <select aria-label={fd.k} value={f[fd.key] ?? ''} onChange={(e) => setF({ ...f, [fd.key]: e.target.value })}>
                      {fd.opts.map(([v, k]) => <option key={v} value={v}>{k}</option>)}
                    </select>
                  </label>
                ))}
                <div className={s.sfFoot}>
                  <span>{rows.length} khớp</span>
                  <button type="button" className={s.sfClear} onClick={() => setF({})}><IconTrash size={16} stroke={1.8} aria-hidden="true" />{t('hr.s.clearFilter')}</button>
                </div>
              </div>
            </>
          )}
        </div>
        <Segmented kind="toggle" label="Kiểu xem nhân sự" value={view} onChange={setView} items={[{ key: 'table', label: 'Bảng' }, { key: 'card', label: 'Thẻ' }]} />
        <span className={s.sfCount}>{count}</span>
        <Button variant="soft" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('hr.s.newStaff')}</Button>
      </div>
      {view === 'card' ? (
        <div className={s.sfCards}>
          {rows.map((x) => (
            <button type="button" key={x.id} className={s.sfCard} onClick={open(x)}>
              <div className={s.sfWho}>
                <span aria-hidden="true" className={s.sfAv}>{x.id.slice(0, 2)}</span>
                <div><b>{x.name}</b><span>{x.id} · {x.dept}</span></div>
              </div>
              <div className={s.sfChips}><Seats st={x} /><span className={`${s.seatChip} ${ST_TONE[x.status].chip}`}>{x.status}</span></div>
              <div className={s.sfMeta}>Vào {x.joined} · {STAFF_CONTRACT[x.id] ?? '—'} · phép còn {fmt1(x.leaveLeft)}</div>
            </button>
          ))}
        </div>
      ) : (
        <section className={s.card} aria-label="Danh sách nhân sự">
          <table className={s.table} aria-label="Danh sách nhân sự">
            <colgroup>
              {['22%', '20%', '15%', '12%', '11%', '11%', '9%'].map((w, i) => <col key={i} style={{ width: w }} />)}
            </colgroup>
            <thead>
              <tr>
                {['Nhân sự', 'Ghế (TitleCode)', 'Phòng', 'Trạng thái', 'Ngày vào', 'Backup', 'Phép còn'].map((c) => <th key={c} scope="col">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.id} className={s.sfRow} onClick={open(x)}>
                  <td>
                    <div className={s.sfWho}>
                      <span aria-hidden="true" className={s.sfAv30}>{x.id.slice(0, 2)}</span>
                      <div><b>{x.name}</b><span>{x.id}</span></div>
                    </div>
                  </td>
                  <td><div className={s.sfChips}><Seats st={x} /></div></td>
                  <td className={s.noteTd}>{x.dept}</td>
                  <td><span className={`${s.stChip} ${ST_TONE[x.status].chip}`}><i className={ST_TONE[x.status].dot} />{x.status}</span></td>
                  <td title={isoOfBoardDate(x.joined) ?? undefined}>{x.joined}</td>
                  <td className={s.noteTd}>{x.backup}</td>
                  <td className={s.right}><b>{fmt1(x.leaveLeft)}</b> <span className={s.mute}>{t('hr.s.days')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
